import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import '../styles/BlogPrimitives.css';

const BLOG_POST_CANONICAL_URL = 'https://circumvent.io/blog/severity-rankings-dont-shrink-risk';
const BLOG_POST_PUBLISHED_AT = '2026-08-24';
const BLOG_POST_READ_TIME = '5 min read';
const BLOG_POST_AUTHOR = 'Circumvent Team';
const FEATURE_IMAGE_SRC = '/images/blog/severity-rankings-dont-shrink-risk.png';
const BLOG_POST_OG_IMAGE_URL = 'https://circumvent.io/images/blog/severity-rankings-dont-shrink-risk.png';
const BLOG_POST_OG_TITLE = "Severity rankings don't shrink risk | Circumvent";
const BLOG_POST_OG_DESCRIPTION =
  'Sorting by reported severity feels like prioritization. Too often it burns engineering time on theoretical risk while verified exposures wait.';

const BlogSeverityRankingsPage = () => (
  <>
    <Helmet>
      <title>{BLOG_POST_OG_TITLE}</title>
      <meta name="description" content={BLOG_POST_OG_DESCRIPTION} />
      <link rel="canonical" href={BLOG_POST_CANONICAL_URL} />
    </Helmet>
    <SeoSocialTags
      title={BLOG_POST_OG_TITLE}
      description={BLOG_POST_OG_DESCRIPTION}
      url={BLOG_POST_CANONICAL_URL}
      ogType="article"
      articlePublishedTime={`${BLOG_POST_PUBLISHED_AT}T12:00:00.000Z`}
      ogImageUrl={BLOG_POST_OG_IMAGE_URL}
    />

    <main className="page page--light blog-post-page">
      <section className="page-section page-section--white" aria-labelledby="post-heading">
        <article className="page-container blog-post">
          <div className="blog-post__header">
            <Link to="/blog" className="blog-crumb-link">
              <span aria-hidden="true">←</span> Blog
            </Link>
            <h1 id="post-heading" className="blog-post__title">
              Severity rankings don&apos;t shrink risk
            </h1>
            <p className="blog-post__lead">
              Sorting the backlog by reported severity or asset criticality feels like prioritization. Too often it is
              queue management. Teams burn limited engineering time on findings that look urgent on a scorecard while
              the exposures that would actually change attacker outcomes wait. Overall risk barely moves.
            </p>

            <ul className="blog-meta-list blog-meta-list--header" aria-label="Post details">
              <li>Insights</li>
              <li>
                <time dateTime={BLOG_POST_PUBLISHED_AT}>24 Aug 2026</time>
              </li>
              <li>{BLOG_POST_READ_TIME}</li>
              <li>By {BLOG_POST_AUTHOR}</li>
            </ul>
          </div>

          <div className="blog-post__layout">
            <div className="blog-post__content">
              <figure className="blog-post__feature-image blog-post__feature-image--bleed">
                <img
                  src={FEATURE_IMAGE_SRC}
                  alt="Illustration contrasting a shiny vanity metrics dashboard on the left with a severed attack-path chain representing real risk reduction on the right."
                  width={1536}
                  height={864}
                  loading="eager"
                  decoding="async"
                />
              </figure>

              <p>
                Security programs got very good at finding issues. Continuous threat exposure management (CTEM) and the
                scanners behind it surface weaknesses at a pace no human triage board can match. The next instinct is
                familiar: rank by CVSS, criticality tags, or whatever severity the tool stamped on the ticket, then push
                the top of that list into engineering.
              </p>
              <p>That instinct is rational. It is also incomplete.</p>
              <p>
                Reported criticality is a label on a finding. Most severity models rate a problem in isolation, against a
                worst-case or highest-impact assumption, without verifying whether that case is true in your environment.
                That is theoretical risk. It is not proof that fixing the item will reduce the risk that matters.
              </p>

              <h2 id="busy-queues-are-not-safer-environments">Busy queues are not safer environments</h2>
              <p>
                Gartner&apos;s recent report on strengthening CTEM with threat-informed defense effectiveness puts the
                core problem plainly. Attackers do not work a vulnerability queue in severity order. They chain what
                works. Teams can close large numbers of exposures and still not know whether the attack paths that matter
                are disrupted.
              </p>
              <p>
                Exposure-led prioritization shows what is exposed and can help validate exploitability. It does not by
                itself prove that the attack paths most likely to create business impact have been broken.
              </p>
              <p>
                A finding can score high and still be the wrong place to spend limited engineering time:
              </p>
              <ul>
                <li>The package is not deployed in the environments you protect.</li>
                <li>The vulnerable function is never called on a live path.</li>
                <li>
                  Compensating controls already bound the blast radius in ways the scanner cannot see.
                </li>
                <li>
                  The asset is &quot;critical&quot; on a spreadsheet, but the exposure does not sit on a workable route to
                  impact.
                </li>
              </ul>
              <p>
                Closing those items improves remediation counts and other vanity metrics. It does not necessarily break
                the path an attacker would use. One open workable path is enough. You can burn engineering capacity,
                watch the severity dashboard improve, and leave that path intact.
              </p>
              <p>
                That is the strategic gap Circumvent is built around. Visibility and ranked queues are hygiene. Decisions
                that protect the business need verified exposure and exploitability, and business context.
              </p>

              <h2 id="criticality-without-verification-wastes-the-scarcest-resource">
                Criticality without verification wastes the scarcest resource
              </h2>
              <p>
                Engineering capacity is finite. Leadership expects product teams to deliver innovation that generates
                business outcomes. Security work that is out of balance starts to impact that delivery.
              </p>
              <p>
                When security sends work ranked mainly by reported severity or static criticality, two failures stack:
              </p>
              <ol>
                <li>
                  Overall risk does not move in proportion to effort. Fixes land on items that never competed for real
                  attacker attention, while higher-impact verified exposures wait.
                </li>
                <li>
                  Trust between security and engineering erodes. Engineers learn that &quot;critical&quot; often means
                  &quot;cleanup,&quot; not &quot;this will change outcomes if we ship it.&quot;
                </li>
              </ol>
              <p>
                We wrote about the second failure in{' '}
                <Link to="/blog/if-its-not-shipped-its-not-fixed">If it&apos;s not shipped, it&apos;s not fixed</Link>.
                Automation and ticket volume that ask teams to absorb unverified or low-confidence work teach a bad
                lesson over time: security PRs and escalations become optional noise. Once that happens, even the
                verified work struggles to get a fair hearing. Friction replaces partnership.
              </p>
              <p>
                Prioritizing unverified high-severity findings is one of the fastest ways to feed that cycle. Whatever
                planning method the team uses (sprints, kanban, or something else), you are burning limited available
                time on work that does not actually matter instead of work that has impact. You are also training
                engineering to not trust the next request.
              </p>

              <h2 id="what-to-prioritize-instead">What to prioritize instead</h2>
              <p>Treat severity and criticality as inputs, not as the decision.</p>
              <p>Before a finding competes for engineering time, ask whether you can defend the ask:</p>
              <ul>
                <li>Is it deployed where it matters?</li>
                <li>
                  Is it reachable in practice, not only theoretically present in a dependency tree?
                </li>
                <li>
                  Does business context (data sensitivity, environment, ownership) change who should care and how fast?
                </li>
                <li>Would fixing it disrupt a real exposure or attack path, or only tidy a score?</li>
                <li>Who can actually merge and operate the change?</li>
              </ul>
              <p>
                Circumvent&apos;s approach is to investigate and enrich findings with that context so Insights that reach
                engineering are verified enough to deserve capacity. Prioritization should follow verified risk and
                ownership, not the loudest scanner label.
              </p>
              <p>
                CTEM remains useful for continuous discovery and exposure management. The programs that improve posture
                use that inventory as raw material, then decide what to fix, defer, or accept based on whether the
                exposure enables real impact. Ranking alone is not that decision.
              </p>

              <h2 id="a-clearer-definition-of-progress">A clearer definition of progress</h2>
              <p>
                If your reporting celebrates tickets closed by severity band, you will optimize for that vanity metric.
                Prefer measures that track whether verified risks were actioned, shipped, and confirmed closed in the
                environments that matter.
              </p>
              <p>
                Activity is easy to report. Outcomes are harder. Engineering teams notice the difference immediately.
                Security leaders feel it later as ignored integrations, stalled remediation, and a backlog that looks
                prioritized while the organization remains exposed on the paths that count.
              </p>
              <p>
                Severity rankings sort work. They do not shrink overall risk on their own. Spend scarce developer time
                on fixes for real, verified risk, or you will buy friction without buying safety.
              </p>

              <div className="blog-post__cta">
                <h3>Prioritization that respects capacity</h3>
                <p>Talk to us about prioritization that respects verified exposure and engineering capacity.</p>
                <Link to="/#contact" className="btn btn-solid">
                  Book a demo
                </Link>
              </div>

              <Link to="/blog" className="blog-crumb-link">
                <span aria-hidden="true">←</span> Blog
              </Link>
            </div>

            <aside className="blog-post__sidebar" aria-label="Post navigation">
              <nav className="blog-toc" aria-label="On this page">
                <p className="blog-toc__title">On this page</p>
                <ul className="blog-toc__list">
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#busy-queues-are-not-safer-environments">
                      Busy queues are not safer environments
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a
                      className="blog-toc__link"
                      href="#criticality-without-verification-wastes-the-scarcest-resource"
                    >
                      Criticality without verification wastes the scarcest resource
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#what-to-prioritize-instead">
                      What to prioritize instead
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#a-clearer-definition-of-progress">
                      A clearer definition of progress
                    </a>
                  </li>
                </ul>
              </nav>
            </aside>
          </div>
        </article>
      </section>
    </main>
  </>
);

export default BlogSeverityRankingsPage;
